import type { AsyncDataOptions, NuxtApp } from '#app'

import type {
  CmsFooterQuery,
  CmsPaymentIconsQuery,
  Page,
  Seo,
  TrustCertificatesCollection,
  TrustTeaserCarouselsCollection,
} from '../../shared/@types/generated/cms/graphql-schema-types'
import type { CustomFaqLotteries, CustomFaqScratchcards } from '../../shared/types'

type ContentReturn<T> = ReturnType<typeof transform<T>>

type Options<T> = Pick<AsyncDataOptions<T>, 'immediate' | 'lazy'>

const isExpired = (expirationDate: number) => new Date(expirationDate).getTime() < Date.now()

const getCachedData = (key: string, nuxtApp: NuxtApp) => {
  const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]
  if (data && isExpired(data.expirationDate)) {
    try {
      // oxlint-disable-next-line typescript/no-dynamic-delete
      delete nuxtApp.payload.data[key]
      return
    } catch {
      /* empty */
    }
  }
  if (!data) return
  return data
}

// Client keeps whole collections across navigations, while the payload only gets the selected part (SF-6396)
const clientCollections = new Map<string, Promise<unknown>>()

const fetchClientCollection = (url: string) => {
  const cached = clientCollections.get(url)
  if (cached) return cached

  const request = $fetch(url).catch((error) => {
    clientCollections.delete(url)
    throw error
  })
  clientCollections.set(url, request)
  return request
}

const sanitize = <T>(input: T) => JSON.parse(JSON.stringify(input).replace(/\\"/g, '“')) as T

export const transform = <T>(input: T): T & { expirationDate: number } => {
  const DAYS_UNTIL_EXPIRATION = 7
  const now = new Date()
  const expirationDate = now.setDate(now.getDate() + DAYS_UNTIL_EXPIRATION)

  // Prevents issues with not closed quotes in JSON content
  return { ...sanitize(input), expirationDate }
}

export const useContent = () => {
  const licensedTerritory = useLicensedTerritory({ case: 'snake' })
  const { currentLocale } = useLanguageUtils()

  const currentLocaleSnake = computed(() => currentLocale.value.replace(/-/g, '_'))

  const { log: datadogLog } = useDatadog()
  const {
    app: { cdnURL },
  } = useRuntimeConfig()
  const isServer = getIsServer()

  // Server reads in-process via server/routes/_content instead of a roundtrip through the public origin (SF-6396)
  const getBaseUrl = () => (isServer ? '/_content' : `${cdnURL}/__content__`)

  const getPathByContentId = (collection: string, id: string) =>
    `${getBaseUrl()}/${licensedTerritory}/${currentLocaleSnake.value}/${collection}/${id.toLowerCase()}.json`

  const getContentById = <ReturnType>(
    collection: string,
    rawId: string,
    options: Options<ReturnType> = {},
  ) => {
    const id = rawId.toLowerCase()

    return useAsyncData<ContentReturn<ReturnType>>(
      computed(() => `${collection}/${id}/${licensedTerritory}/${currentLocaleSnake.value}`),
      (_nuxtApp, { signal }) =>
        $fetch(getPathByContentId(collection, id), { signal })
          .catch((error) => {
            if (error?.response?.status !== 404) {
              datadogLog('error', {
                component: 'useContent.ts',
                error,
                message: `[content] Failed to getContentById(${collection}, ${id})`,
              })
            }
            // Rethrow so asyncData exposes the error instead of transforming undefined (SF-6396)
            throw error
          })
          .then((data) => data as ContentReturn<ReturnType>),
      {
        getCachedData,
        lazy: getIsClient(),
        transform,
        ...options,
      },
    )
  }

  const getUniqueContent = <ReturnType, Selected = ReturnType>(
    collection: string,
    select?: { key: string; fn: (data: ReturnType) => Selected },
  ) => {
    const getFilePath = () =>
      `${getBaseUrl()}/${licensedTerritory}/${currentLocaleSnake.value}/${collection}.json`

    return useAsyncData<ContentReturn<ReturnType>, unknown, ContentReturn<Selected>>(
      computed(
        () =>
          `${collection}/${select ? `${select.key}/` : ''}${licensedTerritory}/${currentLocaleSnake.value}`,
      ),
      (_nuxtApp, { signal }) =>
        (select && !isServer
          ? fetchClientCollection(getFilePath())
          : $fetch(getFilePath(), { signal })
        )
          .catch((error) => {
            if (error?.response?.status !== 404) {
              datadogLog('error', {
                component: 'useContent.ts',
                error,
                message: `[content] Failed to getUniqueContent(${collection})`,
              })
            }
            // Rethrow so asyncData exposes the error instead of transforming undefined (SF-6396)
            throw error
          })
          .then((data) => data as ContentReturn<ReturnType>),
      {
        getCachedData,
        lazy: getIsClient(),
        transform: (input) =>
          transform(select && input ? select.fn(input) : input) as ContentReturn<Selected>,
      },
    )
  }

  const getFaq = (id: string, options?: Options<Page>) =>
    getContentById<CustomFaqLotteries | CustomFaqScratchcards>('faqs', id, options)

  const getFooter = () => getUniqueContent<CmsFooterQuery['footerCollection']>('footer')

  const getPage = (id: string, options?: Options<Page>) => {
    const normalizedId = id.startsWith('/') ? id.slice(1) : id
    return getContentById<Page>('pages', normalizedId, options)
  }

  const fetchPage = (id: string) => $fetch<Page>(getPathByContentId('pages', id)).then(sanitize)

  const getPaymentIcons = () =>
    getUniqueContent<CmsPaymentIconsQuery['paymentIconCollection']>('payment-icons')

  const getPrizes = (id: string, options?: Options<Page>) =>
    getContentById<Page>('prizes', id, options)

  const getRules = (id: string, options?: Options<Page>) =>
    getContentById<Page>('rules', id, options)

  // Keeps only the entry of the current path to avoid the whole seo.json in every payload (SF-6396)
  const getSeo = (path: string) =>
    getUniqueContent<{ items: Seo[] }, Maybe<Seo>>('seo', {
      fn: ({ items }) => items?.find((item) => item.url === path) ?? null,
      key: path,
    })

  const getTrustTeaserCarousels = () =>
    getUniqueContent<TrustTeaserCarouselsCollection>('trust-teaser-carousels')

  const getTrustCertificates = () =>
    getUniqueContent<TrustCertificatesCollection>('trust-certificates')

  return {
    fetchPage,
    getFaq,
    getFooter,
    getPage,
    getPaymentIcons,
    getPrizes,
    getRules,
    getSeo,
    getTrustCertificates,
    getTrustTeaserCarousels,
  }
}
